import Image from 'next/image';
import React, { ReactNode, useState } from 'react';
import styled, { css } from 'styled-components';

import DepartmentEventIcon from '@/components/atom/icon/DepartmentEventIcon';
import HeaderSearch from '@/components/atom/icon/HeaderSearch';
import LinkTag from '@/components/common/LinkTag';
import useModal from '@/hooks/useModal';
import theme from '@/styles/theme';
import { ROUTES } from '@/types/common';
import { MedicalCategoryVo } from '@/types/hospital';
import { MODAL_BUTTON_TYPE, MODAL_TYPE } from '@/types/modal';
import { isEventLabel } from '@/utils/format';

interface Props {
  children: ReactNode;
}
interface InnerProps {
  text: string;
}
interface OnClickProps extends InnerProps {
  onClick: () => void;
}
interface CategoryInnerProps {
  category: MedicalCategoryVo;
  onClick: () => void;
  isMobile?: boolean;
}

interface EventProps {
  onSubmit: (keyword: string) => void;
}

const FindHospitalCategories = ({ children }: Props) => {
  return <Wrapper>{children}</Wrapper>;
};

export default FindHospitalCategories;

const Title = ({ text }: InnerProps) => {
  return <FindHospitalTitle>{text}</FindHospitalTitle>;
};
const SupportText = ({ text }: InnerProps) => {
  return <FindHospitalSupportText>{text}</FindHospitalSupportText>;
};
const Categories = ({ children }: Props) => {
  return <CategoriesWrapper>{children}</CategoriesWrapper>;
};

const Category = ({ category, onClick, isMobile }: CategoryInnerProps) => {
  return (
    <LinkTag
      href={`${ROUTES.HOSPITAL.NEIGHBORHOOD.path}?keyword=${category.name}&regionCode=11&type=all`}
      onClickNavigateToScreen={onClick}
    >
      <CategoryWrapper>
        {isEventLabel(category) && (
          <EventIconWrapper isMobile={isMobile}>
            <DepartmentEventIcon />
          </EventIconWrapper>
        )}
        <div style={{ padding: '20%', width: '100%', height: '100%' }}>
          <Image
            className='department-image'
            src={category.iconUrl}
            width={'100%'}
            height={'100%'}
            loading='eager'
            layout='responsive'
            alt={category.name}
            objectFit='contain'
            objectPosition={'center'}
          />
        </div>
        <p>{category.name}</p>
      </CategoryWrapper>
    </LinkTag>
  );
};

const MoreSee = ({ text, onClick }: OnClickProps) => {
  return (
    <MoreSeeButton type='button' onClick={onClick}>
      <div style={{ padding: '20%', width: '100%', height: '100%' }}>
        <Image
          className='department-image'
          src={'https://doctornow-front-images.s3.ap-northeast-2.amazonaws.com/f2f_all_category.png'}
          width={'100%'}
          height={'100%'}
          loading='eager'
          layout='responsive'
          alt={'전체보기'}
          objectFit='contain'
          objectPosition={'center'}
        />
      </div>
      <p>{text}</p>
    </MoreSeeButton>
  );
};

const Search = ({ onSubmit }: EventProps) => {
  const { showModal, closeModal } = useModal();
  const [keyword, setKeyword] = useState('');

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setKeyword(value);
  };

  const submitSearch = () => {
    const inputKeyword = keyword.trim();

    if (!inputKeyword) {
      showModal({
        type: MODAL_TYPE.ALERT,
        props: {
          title: '검색어를 입력해주세요.',
          buttons: [
            {
              type: MODAL_BUTTON_TYPE.BOLD,
              label: '확인',
              onClick: () => closeModal(),
            },
          ],
        },
      });

      return;
    }

    if (inputKeyword.length < 2) {
      showModal({
        type: MODAL_TYPE.ALERT,
        props: {
          title: '2글자 이상 입력해 주세요.',
          message: '더 정확도 높은 결과를 볼 수 있어요.',
          buttons: [
            {
              type: MODAL_BUTTON_TYPE.BOLD,
              label: '확인',
              onClick: () => closeModal(),
            },
          ],
        },
      });

      return;
    }

    onSubmit(inputKeyword);
  };
  const pressEnterSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;

    e.preventDefault();
    submitSearch();
  };
  return (
    <SearchWrapper>
      <SearchInput
        placeholder='병원, 지역/지하철명+과목, 증상'
        value={keyword}
        onChange={onChange}
        onKeyDown={pressEnterSubmit}
      />
      <SearchButton type='button' onClick={submitSearch}>
        <HeaderSearch size={16} fill={theme.color.gray600} />
      </SearchButton>
    </SearchWrapper>
  );
};

FindHospitalCategories.Title = Title;
FindHospitalCategories.SupportText = SupportText;
FindHospitalCategories.Categories = Categories;
FindHospitalCategories.Category = Category;
FindHospitalCategories.MoreSee = MoreSee;
FindHospitalCategories.Search = Search;

const Wrapper = styled.div`
  padding: 16px 0 24px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const FindHospitalTitle = styled.h2`
  ${({ theme }) => css`
    ${theme.typography.UI19SB}
    color: ${theme.color.gray700};
    display: flex;
    align-items: center;
    padding: 0 24px;
  `}
`;

const FindHospitalSupportText = styled.p`
  ${({ theme }) => css`
    ${theme.typography.UI13R}
    color: ${theme.color.gray500};
    display: flex;
    align-items: center;
    padding: 0 24px;
    margin-top: 4px;
  `}
`;

const CategoryWrapper = styled.li`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  align-self: stretch;

  & > p {
    ${({ theme }) => css`
      ${theme.typography.UI15M};
      @media screen and (max-width: ${theme.breakPoint.SMALL_MOBILE}) {
        ${theme.typography.UI13M}
      }
      color: ${theme.color.gray800};
    `}
  }
`;

const CategoriesWrapper = styled.ul`
  ${({ theme }) => css`
    display: grid;
    flex-wrap: wrap;
    overflow: hidden;
    padding: 0 24px;
    text-wrap: nowrap;
    row-gap: 8px;
    @media screen and (min-width: ${theme.breakPoint.SMALL_MOBILE}) {
      column-gap: 12px;
    }
    margin-top: ${() => {
      const { client } = theme;
      if (client.isMobile) {
        return '12px';
      }
      return '14px';
    }};
    grid-template-columns: ${() => {
      const { client } = theme;
      if (client.isMobile) {
        return 'repeat(5, minmax(calc(20% - 12px), auto));';
      }
      return 'repeat(8, minmax(calc(12.5% - 12px), auto));';
    }};
    grid-template-rows: ${() => {
      const { client } = theme;
      if (client.isMobile) {
        return '1fr 1fr 1fr';
      }
      return '1fr 1fr';
    }};
  `}
`;

const MoreSeeButton = styled.button`
  ${({ theme }) => `
    ${theme.typography.UI15M}
    @media screen and (max-width: ${theme.breakPoint.SMALL_MOBILE}) {
      ${theme.typography.UI13M}
    }
    color: ${theme.color.gray800};
  `}
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const EventIconWrapper = styled.div<{ isMobile?: boolean }>`
  position: absolute;
  right: ${({ isMobile }) => (isMobile ? 0 : 17)}px;
  top: ${({ isMobile }) => (isMobile ? 0 : 5)}px;
  z-index: 1;
`;

const SearchInput = styled.input`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0 48px 0 16px;
  height: 48px;
  border-radius: 8px;
  border: none;
  outline: none;
  background-color: ${theme.color.gray30};
  ${({ theme }) => css`
    ${theme.typography.UI16M}
    color: ${theme.color.gray600};
    &::placeholder {
      ${theme.typography.UI16R}
      color: ${theme.color.gray300};
    }
  `}
`;

const SearchWrapper = styled.div`
  padding: 0 24px;
  margin-bottom: 24px;
  position: relative;
  width: 100%;
  height: 48px;
`;

const SearchButton = styled.button`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 28px;
  height: 28px;
  right: 36px;
  top: 50%;
  transform: translateY(-50%);
`;
